import React, { useState } from 'react';
import {
  useMediaQuery,
  Button,
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  CircularProgress,
} from '@mui/material';
import { useAccount } from '../../../../contexts/Account';
import AdvisoryCloudLogo from './../../../AdvisoryCloudLogo';
import { requestAdvisorRateSuggestions } from '../../../../services/advisor_services';
import ACSnackbar from './../../../ACSnackbar';

const useStyles = ({ isXS }) => ({
  floatingButton: {
    bottom: '20px',
    left: '50%',
    width: '400px',
    transform: 'translateX(-50%)',
    height: '36px',
    fontSize: '15px',
  },
  stickyButton: {
    bottom: 0,
    left: 0,
    width: '100%',
    textAlign: 'center',
    borderRadius: '0px',
    height: '44px',
    fontSize: '16px',
    margin: '0 auto',
    transform: 'translateX(0)',
  },
  dialogPaper: {
    borderRadius: '8px',
    maxWidth: '882px',
  },
  logoBox: {
    textAlign: 'center',
    paddingTop: '24px',
    paddingBottom: isXS ? 0 : '26px',
  },
  title: {
    fontWeight: 500,
    fontSize: '20px',
    textAlign: 'center',
    marginBottom: '4px',
    letterSpacing: '0.75px',
    lineHeight: '24px',
  },
  subtitle: {
    fontStyle: 'italic',
    fontSize: '16px',
    textAlign: 'center',
    marginBottom: '24px',
    lineHeight: '24px',
    letterSpacing: '0.75px',
    fontWeight: 400,
  },
  fieldGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    width: '100%',
    marginBottom: '16px',
  },
  orText: {
    textAlign: isXS ? 'center' : 'left',
    margin: '16px 0',
    color: '#000000',
    fontWeight: 400,
    fontSize: '24px',
    lineHeight: '24px',
    letterSpacing: '0.75px',
  },
  label: {
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: '0.75px',
    marginBottom: '0px',
    display: 'block',
    width: '350px',
  },
  submitButton: {
    marginTop: '24px',
    marginBottom: '16px',
    width: isXS ? '90%' : '330px',
    height: '53px',
    textTransform: 'none',
    fontSize: '16px',
    borderRadius: '10px',
    display: 'flex',
    mx: 'auto',
    weight: 600,
    fontSize: '20px',
  },
});

function SuggestionRateButton() {
  const isXXS = useMediaQuery((theme) => theme.breakpoints.down('xxs'));
  const isXS = useMediaQuery((theme) => theme.breakpoints.down('xs'));
  const isSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const isMD = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const isLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));
  const isXL = useMediaQuery((theme) => theme.breakpoints.down('xl'));
  const { accountDetails, isFreemium } = useAccount();
  const classes = useStyles({ isXS });
  const [open, setOpen] = useState(false);
  const [linkedin, setLinkedin] = useState('');
  const [bio, setBio] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorSnackbar, setErrorSnackbar] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [severityMessage, setSeverityMessage] = useState('success');

  const handleClick = () => setOpen(true);
  const handleClose = () => setOpen(false);

  async function handleSubmit() {
    setIsLoading(true);
    try {
      const result = await requestAdvisorRateSuggestions({
        advisorId: accountDetails.id,
        linkedInUrl: linkedin,
        resume: bio,
      });
      if (result.data.requestAdvisorRateSuggestions) {
        setErrorMessage(
          'Success! Our team will email you the suggestions within 2 business days.'
        );
        setSeverityMessage('success');
      } else {
        setErrorMessage('Failed to send request.');
        setSeverityMessage('error');
      }
      setErrorSnackbar(true);
      setLinkedin('');
      setBio('');
      setIsLoading(false);
      setOpen(false);
    } catch (err) {
      console.error('Error requesting advisor rate suggestions:', err);
    }
  }

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          bottom: '0px',
          left: '0px',
          width: isXXS
            ? '100%'
            : isXS
            ? '100%'
            : isSM
            ? '100%'
            : isMD
            ? '80%'
            : isLG
            ? '80%'
            : isXL
            ? '70%'
            : '60%',
        }}
      >
        <Button
          variant="contained"
          color="primary"
          fullWidth={isXS}
          id="get-advisor-rate-suggestions"
          sx={{
            position: 'absolute',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: isXS ? '100%' : '330px',
            fontWeight: 600,
            ...(isXS ? classes.stickyButton : {}),
          }}
          onClick={handleClick}
        >
          Get Advisor Rate Suggestions
        </Button>
      </Box>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: classes.dialogPaper }}
      >
        <Box sx={classes.logoBox}>
          <AdvisoryCloudLogo
            removeHyperlink
            onClick={() => {}}
            width={190}
            height={34}
          />
        </Box>

        <DialogContent sx={{ p: isXS ? '10px' : '0px 80px' }}>
          <Typography sx={classes.title}>
            Let our team provide suggestions for your Advisor Rate.
          </Typography>
          <Typography sx={classes.subtitle}>
            Please provide a little information to assist our team.
          </Typography>

          <Box
            sx={{
              ...classes.fieldGroup,
              ...(isXS
                ? { flexDirection: 'column', alignItems: 'flex-start' }
                : {}),
            }}
          >
            <Typography
              component="label"
              htmlFor="linkedin-url"
              sx={{
                ...classes.label,
                width: !isXS ? '350px' : 'auto',
              }}
            >
              What is your LinkedIn URL:
            </Typography>
            <TextField
              id="linkedin-url"
              placeholder="ex: linkedin.com/in/john-smith"
              variant="outlined"
              fullWidth
              value={linkedin}
              onChange={(e) => setLinkedin(e.target.value)}
              sx={{ '& .MuiInputBase-root': { height: 40 } }}
            />
          </Box>

          <Typography sx={classes.orText}>- OR -</Typography>

          <Typography
            component="label"
            htmlFor="bio-field"
            sx={{
              ...classes.label,
              width: !isXS ? '700px' : 'auto',
              marginBottom: '8px',
            }}
          >
            Paste in your biography or resume here:
          </Typography>
          <TextField
            id="bio-field"
            placeholder=""
            variant="outlined"
            multiline
            rows={6}
            fullWidth
            value={bio}
            inputProps={{ maxLength: 10000 }}
            onChange={(e) => setBio(e.target.value)}
            sx={{ mb: '16px' }}
          />

          <Button
            variant="contained"
            color="primary"
            sx={classes.submitButton}
            onClick={handleSubmit}
            disabled={isLoading || !(linkedin || bio)}
            startIcon={isLoading && <CircularProgress size={20} />}
          >
            Submit
          </Button>
        </DialogContent>
      </Dialog>
      <ACSnackbar
        open={errorSnackbar}
        text={errorMessage}
        severity={severityMessage}
        onClose={() => setErrorSnackbar(false)}
        autoHideDuration={6000}
        style={{ marginTop: isFreemium ? '50px' : '0px' }}
      />
    </>
  );
}

export default SuggestionRateButton;
